// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EditablePropertyButton } from '@/components/editable-property-button';
import { LinkifiedText } from '@/components/linkified-text';
import { PopoverForm } from '@/components/popover-form';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useDisclosure } from '@/hooks/use-disclosure';
import { usePopoverAutoFocus } from '@/hooks/use-popover-auto-focus';
import { useTimeout } from '@/hooks/use-timeout';
import { NotesCopyButton } from '@/order/components/notes-copy-button';
import { PurchaseOrder } from '@/purchase-order/api/types';
import { updatePurchaseOrder } from '@/purchase-order/api/update-purchase-order';
import { isCancelled, isReceived } from '@/purchase-order/constants/status';
import {
  purchaseOrderDetailQueryKey,
  purchaseOrdersQueryKey,
} from '@/purchase-order/query-keys';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const EMPTY_STATE = 'Click to add notes...';

const formSchema = z.object({ orderNotes: z.string().nullable() });

type FormData = z.infer<typeof formSchema>;

interface Props {
  purchaseOrder: Pick<PurchaseOrder, 'key' | 'orderNotes' | 'status'>;
  isDestinationThirdParty?: boolean;
}

export function PurchaseOrderNotesControl(props: Props) {
  const theme = useTheme();
  const editor = useDisclosure();
  const [expanded, setExpanded] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = usePopoverAutoFocus(editor.isOpen);
  const queryClient = useQueryClient();

  const { formState, register, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { orderNotes: props.purchaseOrder.orderNotes ?? '' },
  });

  // Reset form with latest data from props
  const resetFormWithLatestData = () => {
    reset({ orderNotes: props.purchaseOrder.orderNotes ?? '' });
  };

  const updatePurchaseOrderMutation = useMutation({
    mutationFn: (orderNotes: string | null) =>
      updatePurchaseOrder(props.purchaseOrder.key, { orderNotes }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrderDetailQueryKey(props.purchaseOrder.key),
      });
      queryClient.invalidateQueries({ queryKey: purchaseOrdersQueryKey });
      // Reset form with latest data before closing
      resetFormWithLatestData();
      editor.close();
    },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.short);

  const { isDirty } = formState;

  const handleOpen = () => {
    // Reset form with latest data when opening editor
    resetFormWithLatestData();
    editor.open();
  };

  const handleClose = () => {
    editor.close();
    timeout.start();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (isDirty) {
      // Transform empty string to null before sending to API
      const orderNotes =
        data.orderNotes?.trim() === '' ? null : data.orderNotes;
      return updatePurchaseOrderMutation.mutate(orderNotes);
    }

    editor.close();
  };

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  const notes = props.purchaseOrder.orderNotes ?? EMPTY_STATE;
  const color =
    props.purchaseOrder.orderNotes === null ? 'text.secondary' : 'text.primary';
  const hasNotes =
    props.purchaseOrder.orderNotes !== null &&
    props.purchaseOrder.orderNotes.trim() !== '';

  const isLongNotes = notes.length > 200 || notes.split('\n').length > 4;

  // Disable editing if:
  // 1. Order is cancelled or received, OR
  // 2. Destination is 3PL and status is not draft (already sent to 3PL)
  const is3PLLocked =
    props.isDestinationThirdParty === true &&
    props.purchaseOrder.status !== 'DRAFT';

  const isDisabled =
    isCancelled(props.purchaseOrder.status) ||
    isReceived(props.purchaseOrder.status) ||
    is3PLLocked;

  const tooltipTitle = is3PLLocked
    ? 'This has already been sent to your 3PL provider and can no longer be edited'
    : '';

  return (
    <>
      <Tooltip title={tooltipTitle} arrow>
        <span style={{ display: 'block', height: '100%' }}>
          <Box sx={{ height: '100%', position: 'relative' }}>
            <EditablePropertyButton
              ref={buttonRef}
              component="div"
              onClick={isDisabled ? undefined : handleOpen}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                minHeight: '5rem',
                height: '100%',
                alignItems: 'start',
                justifyContent: 'start',
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                borderLeft: '2px solid',
                borderColor: 'divider',
                borderRadius: 0,
                marginLeft: 0,
                position: 'relative',
                cursor: isDisabled ? 'default' : 'pointer',
                '& .edit-icon': {
                  position: 'absolute',
                  top: '0.4rem',
                  right: hasNotes ? '2.3rem' : '0.3rem',
                  margin: 0,
                  opacity: isDisabled ? 0.3 : 1,
                },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  position: 'relative',
                }}
              >
                <Typography
                  variant="body2"
                  textAlign="left"
                  marginRight="auto"
                  whiteSpace="pre-wrap"
                  color={color}
                  sx={{
                    display: '-webkit-box',
                    paddingRight: hasNotes ? 6 : 0,
                    WebkitLineClamp: expanded ? 'unset' : 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: expanded ? 'auto' : 'hidden',
                    textOverflow: 'ellipsis',
                    maxHeight: expanded ? '20rem' : 'unset',
                    overflowY: expanded ? 'auto' : 'hidden',
                  }}
                >
                  <LinkifiedText>{notes}</LinkifiedText>
                </Typography>
                {isLongNotes && (
                  <Typography
                    component="span"
                    variant="body2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleExpand();
                    }}
                    sx={{
                      fontStyle: 'italic',
                      color: 'primary.main',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {expanded ? 'read less' : 'read more'}
                  </Typography>
                )}
              </Box>
            </EditablePropertyButton>
            {hasNotes && props.purchaseOrder.orderNotes !== null && (
              <NotesCopyButton notes={props.purchaseOrder.orderNotes} />
            )}
          </Box>
        </span>
      </Tooltip>
      <SupportSnackbar
        isOpen={updatePurchaseOrderMutation.isError}
        onClose={updatePurchaseOrderMutation.reset}
        message="An error occurred while updating the purchase order."
      />
      <PopoverForm
        verticalOffset={24}
        horizontalOffset={8}
        position="left"
        verticalPosition="top"
        anchorElement={buttonRef.current}
        isOpen={editor.isOpen}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmission)}
      >
        <TextField
          autoFocus
          inputRef={inputRef}
          multiline
          rows={3}
          size="small"
          InputProps={register('orderNotes')}
          sx={{ width: '20rem' }}
        />
      </PopoverForm>
    </>
  );
}
